import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X, ChevronDown, ChevronRight } from "lucide-react";
import { healthRecommendationAPI, referenceDataAPI } from "../services/api";

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.split("T")[0].split(" ")[0];
  let parts = null;

  if (dateOnly.includes("-")) {
    parts = dateOnly.split("-");
  } else if (dateOnly.includes("/")) {
    parts = dateOnly.split("/");
  }

  if (parts?.length === 3) {
    let year;
    let month;
    let day;

    if (parts[0].length === 4) {
      year = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      day = Number.parseInt(parts[2], 10);
    } else {
      day = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      year = Number.parseInt(parts[2], 10);
    }

    if (year > 2400) {
      year -= 543;
    } else if (year < 100) {
      year += 2000;
    }

    const parsed = new Date(year, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const calculateAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) {
    return null;
  }

  let age = examDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = examDate.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && examDate.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  if (age < 0 || age > 150) {
    return null;
  }

  return age;
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const Container = styled.div`
  width: 95vw;
  max-width: 1400px;
  height: 90vh;
  max-height: 90vh;
  overflow-y: auto;
  background-color: white;
  position: relative;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
    Arial, sans-serif;
  color: #1d1d1f;
  padding: 20px 40px;
  border-radius: 12px;
`;

const Header = styled.div`
  text-align: center;
  padding: 20px;
  background-color: white;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 30px;
  height: 30px;
  border: none;
  background: none;
  font-size: 24px;
  color: #8e8e93;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #1d1d1f;
  }
`;

const Title = styled.h1`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin-top: 10px;
`;

const PatientInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #cfd3d4;
  margin: 20px 0;
`;

const PatientDetails = styled.div`
  font-size: 16px;
  color: #6f6f6f;
  font-weight: 400;
  line-height: 1.5;

  strong {
    color: #6f6f6f;
    font-weight: 700;
  }
`;

const HNNumber = styled.div`
  font-size: 16px;
  color: #6f6f6f;
  font-weight: 400;
`;

const RecordsList = styled.div`
  padding: 20px 0;
`;

const RecordItem = styled.div`
  background-color: white;
  border: 1px solid #cfd3d4;
  border-radius: ${(props) => (props.isExpanded ? "8px 8px 0 0" : "8px")};
  ${(props) =>
    props.isExpanded ? "margin-bottom: 0;" : "margin-bottom: 12px;"}
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9f9fb;
  }

  &:active {
    background-color: #f0f0f2;
  }
`;

const RecordContent = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 30px;
  align-items: center;
`;

const RecordDate = styled.div`
  font-size: 16px;
  color: #a3a3a3;
  font-weight: 400;
`;

const RecordTime = styled.div`
  font-size: 16px;
  color: #a3a3a3;
  font-weight: 400;
`;

const RecordDetails = styled.div`
  font-size: 16px;
  color: #a3a3a3;
  font-weight: 400;
`;

const Arrow = styled.div`
  color: #000000;
  font-size: 29px;
  width: 29px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 15px;
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.isExpanded ? "rotate(90deg)" : "rotate(0deg)"};
`;

const ExpandedContent = styled.div`
  padding: 1.5rem;
  background-color: white;
  border: 1px solid #cfd3d4;
  border-top: none;
  border-radius: 0 0 8px 8px;
  margin-bottom: 12px;
  display: ${(props) => (props.isExpanded ? "block" : "none")};
`;

const PatientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 3fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ececec;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoItem = styled.div`
  font-size: 14px;
  color: #6f6f6f;
`;

const InfoLabel = styled.span`
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-weight: 400;
`;

const HNValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6f6f6f;
`;

const ParameterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ParameterTable = styled.div`
  display: grid;
  grid-template-columns: 80px 80px 80px 50px;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const CategoryHeader = styled.div`
  grid-column: 1 / -1;
  padding: 0.5rem 0 0.25rem 0;
  font-weight: 700;
  font-size: 11px;
  color: #4a4a4a;
  text-align: left;
  border-top: ${(props) => (props.isFirst ? "none" : "1px solid #e0e0e0")};
  margin-top: ${(props) => (props.isFirst ? "0" : "0.5rem")};
`;

const ParameterTableHeader = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #6f6f6f;
  text-align: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ececec;
`;

const ParameterTableCell = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #6f6f6f;
  text-align: center;
  padding: 0.25rem 0;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: transparent;
  color: ${(props) => {
    if (props.status === "high") return "#DC2626";
    if (props.status === "low") return "#D97706";
    return "#059669";
  }};
`;

const TemplateTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  border: 1px solid #00b5b8;
  border-radius: 0px;
  padding: 4px 8px;
  margin: 2px;
  font-size: 14px;
  color: #00b5b8;
  cursor: pointer;
  position: relative;
  width: fit-content;
`;

const RecommendationSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
`;

const RecommendationColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RecommendationTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #000000;
  margin: 0 0 1rem 0;
`;

const RecommendationImage = styled.div`
  width: 58px;
  height: 58px;
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  margin-bottom: 0.5rem;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const RecommendationText = styled.div`
  font-size: 14px;
  color: #6f6f6f;
  line-height: 1.5;
  min-height: 120px;
`;

const HealthRecordsHistory = ({ isOpen, onClose, selectedRecord }) => {
  const displayAge =
    calculateAgeAtDate(
      selectedRecord?.birthday ||
        selectedRecord?.birthdate ||
        selectedRecord?.birth_date,
      selectedRecord?.rawDate ||
        selectedRecord?.dateadd ||
        selectedRecord?.date,
    ) ?? selectedRecord?.age;
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterData, setMasterData] = useState(null);

  const compareValues = (normalRange, resultValue) => {
    const parseComparableRange = (value) => {
      if (value === null || value === undefined) {
        return null;
      }

      const text = String(value).trim().replace(/,/g, "").replace(/\s+/g, "");
      const cleanedText = text.replace(
        /(cells\/hpf|cells\/lpf|\/hpf|\/lpf|cells\/μl|cells\/ul|cells\/ยตl)$/i,
        "",
      );

      const rangeMatch = cleanedText.match(
        /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)/,
      );
      if (rangeMatch) {
        return {
          min: parseFloat(rangeMatch[1]),
          max: parseFloat(rangeMatch[2]),
        };
      }

      const lessThanMatch = cleanedText.match(/^<(-?\d+(?:\.\d+)?)/);
      if (lessThanMatch) {
        return {
          min: Number.NEGATIVE_INFINITY,
          max: parseFloat(lessThanMatch[1]),
        };
      }

      const greaterThanMatch = cleanedText.match(/^>(-?\d+(?:\.\d+)?)/);
      if (greaterThanMatch) {
        return {
          min: parseFloat(greaterThanMatch[1]),
          max: Number.POSITIVE_INFINITY,
        };
      }

      const singleValueMatch = cleanedText.match(/^(-?\d+(?:\.\d+)?)/);
      if (singleValueMatch) {
        const num = parseFloat(singleValueMatch[1]);
        return {
          min: num,
          max: num,
        };
      }

      return null;
    };

    if (
      !normalRange ||
      !resultValue ||
      normalRange === "xxx" ||
      resultValue === "xxx"
    ) {
      return { status: "normal", label: "-" };
    }

    // ✅ ถ้า normalRange เป็น text (มีตัวอักษร แต่ไม่ใช่ format "M x-y, F x-y")
    // ต้องเช็คก่อน parseFloat เพราะ parseFloat("3+(300mg/dL)") = 3 ทำให้หลุดรอดไปเป็น Normal
    const normalizedRangeText = normalRange
      .trim()
      .replace(/,/g, "")
      .replace(/\s+/g, "");
    const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRange.trim());
    const looksNumericRange = /^[-<>]?\d/.test(normalizedRangeText);
    if (
      /[a-zA-Z]/.test(normalRange.trim()) &&
      !normalRangeIsGenderFormat &&
      !looksNumericRange
    ) {
      if (
        resultValue.trim().toLowerCase() === normalRange.trim().toLowerCase()
      ) {
        return { status: "normal", label: "Normal" };
      } else {
        return { status: "high", label: "High" };
      }
    }

    const normalizedNormalRange = parseComparableRange(normalRange);
    const normalizedResultRange = parseComparableRange(resultValue);
    if (normalizedNormalRange && normalizedResultRange) {
      if (normalizedResultRange.max < normalizedNormalRange.min) {
        return { status: "low", label: "Low" };
      }
      if (normalizedResultRange.min > normalizedNormalRange.max) {
        return { status: "high", label: "High" };
      }
      return { status: "normal", label: "Normal" };
    }

    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: "normal", label: "-" };
    }

    if (normalRange.includes("-")) {
      const [min, max] = normalRange
        .split("-")
        .map((v) => parseFloat(v.trim()));
      if (result < min) {
        return { status: "low", label: "Low" };
      } else if (result > max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRange.startsWith("<")) {
      const max = parseFloat(normalRange.replace("<", "").trim());
      if (result >= max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRange.startsWith(">")) {
      const min = parseFloat(normalRange.replace(">", "").trim());
      if (result <= min) {
        return { status: "low", label: "Low" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    }

    return { status: "normal", label: "Normal" };
  };

  const normalizeParameterName = (paramName) => {
    const mapping = {
      "SGOT (AST)": "SGOT (AST)",
      "SGPT (ALT)": "SGPT (ALT)",
      "ALP (Alkaline Phosphatase)": "ALP",
      ALP: "ALP",
      "Uric Acid": "Uric Acid",
      BUN: "BUN, Urea",
      GGT: "GGT",
      Cholesterol: "คอเลสเตอรอลรวม",
      Triglyceride: "ไตรกลีเซอไรด์",
      CHO: "คอเลสเตอรอลรวม",
      TRI: "ไตรกลีเซอไรด์",
      FBS: "Fasting blood glucose",
      Basophil: "Basophil",
      "Eosinophil %": "Eosinophil",
      Eosinophil: "Eosinophil",
      "Urine Color": "Color",
      "Urine Appearance": "Urine Appearance",
      Appearance: "Urine Appearance",
      Clarity: "Clarity",
      "Urine pH": "pH",
      "Specific Gravity": "Specific Gravity",
      "Urine Protein": "Protein",
      Protein: "Protein",
      "Urine Glucose": "Sugar",
      Glucose: "Fasting blood glucose",
      "Glucose (FBS)": "Fasting blood glucose",
      "Urine Ketone": "Urine Ketone",
      Ketone: "Urine Ketone",
      "Urine Blood": "Blood",
      "Urine Leukocytes": "Leukocytes",
      "Urine Nitrite": "Nitrite",
      "Urine Bilirubin": "Bilirubin",
      Urobilinogen: "Urobilinogen",
      "Urine RBC": "RBC (Urine)",
      "Urine WBC": "WBC (Urine)",
      "Urine Bacteria": "Bacteria",
      Bacteria: "Bacteria",
      "Epithelial Cells": "Epithelial cell",
      Epithelial: "Epithelial cell",
      "Calcium Oxalate": "Calcium oxalate crystal",
      "Calcium oxalate": "Calcium oxalate crystal",
      "Uric Acid Crystal": "Uric acid crystal",
      "Uric acid": "Uric acid crystal",
      "Triple Phosphate": "Triple phosphate crystal",
      "Triple phosphate": "Triple phosphate crystal",
      "Calcium Carbonate": "Calcium Carbonate crystal",
      "Calcium carbonate": "Calcium Carbonate crystal",
      "Calcium Carbonate crystal": "Calcium Carbonate crystal",
      "Calcium Phosphate": "Calcium phosphate crystal",
      "Calcium phosphate": "Calcium phosphate crystal",
      Cystine: "Cystine crystal",
      "Cystine Crystal": "Cystine crystal",
      "Hyaline Cast": "Hyaline cast",
      "Granular Cast": "Granular cast",
      "Cellular Cast": "Cellular cast",
      "Waxy Cast": "Waxy cast",
      "RBC cast": "RBC cast",
      "Stool Color": "Color (Stool)",
      "Stool WBC": "WBC (Stool)",
      "Stool RBC": "RBC (Stool)",
      "Parasite, Ova": "Parasite",
      Parasite: "Parasite",
      Consistency: "Consistency",
      Sodium: "Sodium",
      Potassium: "Potassium",
      Chloride: "Chloride",
      Bicarbonate: "Bicarbonate",
      "Total Calcium": "Total Calcium",
      Calcium: "Total Calcium",
      "Inorganic phosphate": "Inorganic phosphate",
      Magnesium: "Magnesium",
      AFP: "AFP",
      PSA: "PSA",
      CEA: "CEA",
      "CA19-9": "CA19-9",
      CA125: "CA125",
      HBsAb: "HBsAb",
      "Anti-HBs Concentration": "Anti-HBs Concentration",
      "HCV Ab": "HCV Ab",
      "anti-HCV": "HCV Ab",
      "Free T3": "Free T3",
      "Free T4": "Free T4",
      TSH: "TSH",
      Cholinesterase: "Cholinesterase",
      "Direct bilirubin": "Direct bilirubin",
    };
    return mapping[paramName] || paramName;
  };

  const categorizeParameters = (parameters) => {
    if (!masterData || !parameters) {
      return { ทั้งหมด: parameters || [] };
    }

    const categorized = {};
    const usedParams = new Set();

    for (const categoryName in masterData) {
      const categoryParams = masterData[categoryName];
      const paramNamesInCategory = Object.keys(categoryParams);

      categorized[categoryName] = parameters.filter((param) => {
        const isMatch = paramNamesInCategory.some((masterParamName) => {
          const cleanMasterName = masterParamName.replace(/^_hide_/, "");
          const normalizedParamName = normalizeParameterName(param.name);
          const paramLower = normalizedParamName.toLowerCase();
          const masterLower = cleanMasterName.toLowerCase();

          if (masterLower === paramLower) return true;

          // จับคู่แบบมี suffix ในวงเล็บ เช่น "ALP (Alkaline Phosphatase)" -> "ALP"
          // แต่ไม่จับคู่ถ้า suffix เป็น type qualifier เช่น (urine), (stool), (serum), (blood)
          const parenMatch = paramLower.match(/\(([^)]+)\)/);
          const parenContent = parenMatch ? parenMatch[1].trim() : "";
          const typeQualifiers = ["urine", "stool", "serum", "blood"];
          const paramBase = paramLower.split(" (")[0].trim();
          if (
            paramBase === masterLower &&
            !typeQualifiers.includes(parenContent)
          )
            return true;

          if (paramLower === "eosinophil %" && masterLower === "eosinophil")
            return true;
          if (
            paramLower === "epithelial cells" &&
            masterLower === "epithelial cell"
          )
            return true;
          if (paramLower === "epithelial" && masterLower === "epithelial cell")
            return true;

          return false;
        });

        if (isMatch) usedParams.add(param.name);
        return isMatch;
      });
    }

    const unusedParams = parameters.filter(
      (param) => !usedParams.has(param.name),
    );
    if (unusedParams.length > 0) {
      categorized["อื่นๆ"] = unusedParams;
    }

    return Object.fromEntries(
      Object.entries(categorized).filter(([_, params]) => params.length > 0),
    );
  };

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const result = await referenceDataAPI.getAllParameters();
        if (result.status === "success" && result.data) {
          setMasterData(result.data);
        }
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };

    loadMasterData();
  }, []);

  useEffect(() => {
    if (isOpen && selectedRecord?.id) {
      fetchHistory();
    }
  }, [isOpen, selectedRecord?.id]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const recordIdToUse = selectedRecord.dbRecordId || selectedRecord.id;
      const response = await healthRecommendationAPI.getHistory(recordIdToUse);

      if (response.status === "success" && Array.isArray(response.data)) {
        const formattedHistory = response.data.map((item, index) => {
          const sentDate = new Date(item.sent_at);
          return {
            id: item.id,
            date: sentDate
              .toLocaleDateString("th-TH", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
              .replace(/\//g, "/"),
            time:
              sentDate.toLocaleTimeString("th-TH", {
                hour: "2-digit",
                minute: "2-digit",
              }) + " น.",
            doctor: item.sent_by || "ไม่ระบุ",
            templates: item.templates_json
              ? JSON.parse(item.templates_json)
              : [],
            recommendations: {
              doctor: item.doctor_advice || "",
              food: item.nutrition_advice || "",
              exercise: item.exercise_advice || "",
              sleep: item.sleep_advice || "",
            },
          };
        });
        setHistoryRecords(formattedHistory);
      } else {
        setHistoryRecords([]);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      setHistoryRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleRecordClick = (recordId) => {
    setExpandedRecord(expandedRecord === recordId ? null : recordId);
  };

  const records = historyRecords;

  if (!isOpen) return null;

  return (
    <ModalOverlay
      isOpen={isOpen}
      onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
    >
      <Container>
        <Header>
          <CloseButton onClick={handleCloseModal}>
            <X size={20} />
          </CloseButton>
          <Title>ประวัติการส่งผลสุขภาพ</Title>
        </Header>

        <PatientInfo>
          <PatientDetails>
            คุณ{" "}
            <strong>
              {selectedRecord?.patientName?.replace(
                /^(นาย|นางสาว|นาง|ดร\.|ศ\.|รศ\.|ผศ\.)\s*/,
                "",
              ) || "สมชาย ใจดี"}
            </strong>{" "}
            เพศ : <strong>{selectedRecord?.gender || "ชาย"}</strong> อายุ :{" "}
            <strong>{displayAge || "45"}</strong> โรคประจำตัว :{" "}
            <strong>
              {selectedRecord?.diseases || "ความดัน, ไขมันในเลือดสูง"}
            </strong>
          </PatientDetails>
          <HNNumber>HN: {selectedRecord?.hn || "VN-XXXXX-03"}</HNNumber>
        </PatientInfo>

        <RecordsList>
          {loading ? (
            <div
              style={{ textAlign: "center", padding: "2rem", color: "#6F6F6F" }}
            >
              กำลังโหลดประวัติ...
            </div>
          ) : records.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "2rem", color: "#6F6F6F" }}
            >
              ยังไม่มีประวัติการส่งผลสุขภาพ
            </div>
          ) : (
            records.map((record) => (
              <div key={record.id}>
                <RecordItem
                  isExpanded={expandedRecord === record.id}
                  onClick={() => handleRecordClick(record.id)}
                >
                  <RecordContent>
                    <RecordDate>วันที่ {record.date}</RecordDate>
                    <RecordTime>เวลา {record.time}</RecordTime>
                    <RecordDetails>ส่งโดย {record.doctor}</RecordDetails>
                  </RecordContent>
                  <Arrow isExpanded={expandedRecord === record.id}>&gt;</Arrow>
                </RecordItem>

                <ExpandedContent isExpanded={expandedRecord === record.id}>
                  <PatientInfoGrid>
                    <InfoColumn>
                      <InfoItem>
                        <InfoLabel>ชื่อ:</InfoLabel>{" "}
                        <InfoValue>
                          {selectedRecord?.patientName || "สมชาย ใจดี"}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>เพศ:</InfoLabel>{" "}
                        <InfoValue>{selectedRecord?.gender || "ชาย"}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>อายุ:</InfoLabel>{" "}
                        <InfoValue>{displayAge || "45"}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>โรคประจำตัว:</InfoLabel>{" "}
                        <InfoValue>
                          {selectedRecord?.diseases ||
                            "ความดัน, ไขมันในเลือดสูง"}
                        </InfoValue>
                      </InfoItem>
                    </InfoColumn>

                    <InfoColumn>
                      <InfoItem style={{ fontWeight: "400" }}>HN</InfoItem>
                      <HNValue>{selectedRecord?.hn || "VN-XXXXX-03"}</HNValue>
                    </InfoColumn>

                    <ParameterColumn>
                      <ParameterTable>
                        <ParameterTableHeader>พารามิเตอร์</ParameterTableHeader>
                        <ParameterTableHeader>ค่าปกติ</ParameterTableHeader>
                        <ParameterTableHeader>
                          ค่าที่ตรวจวัดได้
                        </ParameterTableHeader>
                        <ParameterTableHeader></ParameterTableHeader>

                        {(() => {
                          const defaultParams = [
                            { name: "CHO", result: "xxx", normal: "xxx" },
                            { name: "TRI", result: "xxx", normal: "xxx" },
                            { name: "HDL", result: "xxx", normal: "xxx" },
                            { name: "LDL", result: "xxx", normal: "xxx" },
                            { name: "CBC", result: "xxx", normal: "xxx" },
                            { name: "FBS", result: "xxx", normal: "xxx" },
                            { name: "SGPT", result: "xxx", normal: "xxx" },
                            { name: "SGOT", result: "xxx", normal: "xxx" },
                            { name: "BUN", result: "xxx", normal: "xxx" },
                            {
                              name: "Creatinine",
                              result: "xxx",
                              normal: "xxx",
                            },
                          ];
                          const params =
                            selectedRecord?.parameters || defaultParams;
                          const categorized = categorizeParameters(params);

                          return Object.entries(categorized).map(
                            ([category, categoryParams], catIndex) => (
                              <React.Fragment key={`cat-${catIndex}`}>
                                <CategoryHeader isFirst={catIndex === 0}>
                                  {category}
                                </CategoryHeader>
                                {categoryParams.map((param, index) => {
                                  const comparison = compareValues(
                                    param.normal,
                                    param.result,
                                  );
                                  return (
                                    <React.Fragment
                                      key={`${catIndex}-${index}`}
                                    >
                                      <ParameterTableCell>
                                        {param.name}
                                      </ParameterTableCell>
                                      <ParameterTableCell>
                                        {param.normal}
                                      </ParameterTableCell>
                                      <ParameterTableCell>
                                        {param.result}
                                      </ParameterTableCell>
                                      <ParameterTableCell
                                        style={{
                                          textAlign: "left",
                                          paddingLeft: "0.25rem",
                                        }}
                                      >
                                        <ComparisonBadge
                                          status={comparison.status}
                                        >
                                          {comparison.label}
                                        </ComparisonBadge>
                                      </ParameterTableCell>
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            ),
                          );
                        })()}
                      </ParameterTable>
                    </ParameterColumn>

                    <InfoColumn>
                      <InfoItem
                        style={{
                          fontWeight: "400",
                          fontSize: "14px",
                          color: "#6F6F6F",
                          marginBottom: "0.5rem",
                        }}
                      >
                        การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น / Doctor Note
                      </InfoItem>
                      <div>
                        {record.templates && record.templates.length > 0 ? (
                          record.templates.map((template, index) => (
                            <TemplateTag key={index}>{template}</TemplateTag>
                          ))
                        ) : (
                          <span style={{ fontSize: "14px", color: "#A3A3A3" }}>
                            ไม่มี template
                          </span>
                        )}
                      </div>
                    </InfoColumn>
                  </PatientInfoGrid>

                  <RecommendationSection>
                    <RecommendationColumn>
                      <RecommendationTitle>
                        คำแนะนำสุขภาพโดยแพทย์
                      </RecommendationTitle>
                      <RecommendationText>
                        {record.recommendations?.doctor || "ไม่มีข้อมูล"}
                      </RecommendationText>
                    </RecommendationColumn>

                    <RecommendationColumn>
                      <RecommendationTitle>
                        แนะนำการรับประทานอาหาร
                      </RecommendationTitle>
                      <RecommendationImage
                        style={{ backgroundImage: "url(food.jpg)" }}
                      ></RecommendationImage>
                      <RecommendationText>
                        {record.recommendations?.food || "ไม่มีข้อมูล"}
                      </RecommendationText>
                    </RecommendationColumn>

                    <RecommendationColumn>
                      <RecommendationTitle>
                        แนะนำการออกกำลังกาย
                      </RecommendationTitle>
                      <RecommendationImage
                        style={{ backgroundImage: "url(exercise.jpg)" }}
                      ></RecommendationImage>
                      <RecommendationText>
                        {record.recommendations?.exercise || "ไม่มีข้อมูล"}
                      </RecommendationText>
                    </RecommendationColumn>

                    <RecommendationColumn>
                      <RecommendationTitle>แนะนำการนอน</RecommendationTitle>
                      <RecommendationImage
                        style={{ backgroundImage: "url(sleep.jpg)" }}
                      ></RecommendationImage>
                      <RecommendationText>
                        {record.recommendations?.sleep || "ไม่มีข้อมูล"}
                      </RecommendationText>
                    </RecommendationColumn>
                  </RecommendationSection>
                </ExpandedContent>
              </div>
            ))
          )}
        </RecordsList>
      </Container>
    </ModalOverlay>
  );
};

export default HealthRecordsHistory;
